import { type SourceWithEntityName, TWELVE_HOURS_MS } from '@netpurpose/types'
import { valueIsDefined } from '@netpurpose/utils'
import {
  keepPreviousData,
  type Query,
  type QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { pickBy } from 'lodash'
import { useMemo } from 'react'
import { apiConfig } from '../../config'
import { type ApiError, getApiErrorMessage } from '../../errors'
import { GeneratedFactApi } from '../../GeneratedApi'
import type {
  Portfolio as BasePortfolio,
  CopyPortfolioRequest,
  CopyPortfolioResponse,
  PortfolioUpdate,
} from '../../generated/facts'
import {
  type CreatePortfolio,
  formatCreatePortfolioRequest,
  type Portfolio,
  type PortfolioType,
  reversePortfolioFieldMap,
  reversePortfolioSnapshotFieldMap,
  reverseSourceFieldMap,
  transformPortfolio,
  transformSource,
} from '../../models'
import { snakeToCamelKeys } from '../../utils'
import { useStreamDownload } from '../useStreamDownload'
import { getPaginationConfig, useUrlApiConnector } from '../useUrlApiConnector'
import {
  ENTITY_BREAKDOWN_BY_FUNDS_QUERY_CACHE_KEY,
  ENTITY_DATA_BY_QUESTION_ID_QUERY_CACHE_KEY,
} from './useEntities'
import {
  PAGINATED_FOF_HOLDINGS_BY_COMPANY_QUERY_CACHE_KEY,
  PAGINATED_HOLDINGS_QUERY_CACHE_KEY,
} from './useHoldings'
import { PORTFOLIO_IMPACT_DATA_QUERY_CACHE_KEY } from './useImpactData'

const PAGINATED_PORTFOLIOS_QUERY_CACHE_KEY = 'portfolios'
const PORTFOLIO_QUERY_CACHE_KEY = 'portfolio'
const PORTFOLIO_SOURCES_QUERY_CACHE_KEY = 'portfolioSources'
const PORTFOLIO_DISCLOSURE_QUERY_CACHE_KEY = 'portfolioDisclosure'
const PORTFOLIO_SDG_ALIGNMENT_OUTCOMES_QUERY_CACHE_KEY = 'portfolioSDGAlignmentOutcomes'
const PORTFOLIO_SDG_ALIGNMENT_REVENUE_WEIGHTED_QUERY_CACHE_KEY =
  'portfolioSDGAlignmentRevenueWeighted'
const PORTFOLIO_SDG_ALIGNMENT_REVENUE_UNWEIGHTED_QUERY_CACHE_KEY =
  'portfolioSDGAlignmentRevenueUnweighted'
const PORTFOLIO_SDI_ALIGNMENT_REVENUE_WEIGHTED_QUERY_CACHE_KEY =
  'portfolioSDIAlignmentRevenueWeighted'
const PORTFOLIO_SDI_ALIGNMENT_REVENUE_UNWEIGHTED_QUERY_CACHE_KEY =
  'portfolioSDIAlignmentRevenueUnweighted'
const PORTFOLIO_SDI_LABELS_WEIGHTED_QUERY_CACHE_KEY = 'portfolioSDILabelsWeighted'
const PORTFOLIO_SDI_LABELS_UNWEIGHTED_QUERY_CACHE_KEY = 'portfolioSDILabelsUnweighted'
const PORTFOLIO_SNAPSHOTS_QUERY_CACHE_KEY = 'portfolioSnapshots'

const getPredicate =
  <T>(keys: T[]) =>
  (query: Query<unknown, Error, unknown, readonly unknown[]>) =>
    keys.filter(valueIsDefined).every((key) => query.queryKey.includes(key))

export const invalidateAllPortfolioQueries = ({
  queryClient,
  portfolioId,
}: {
  queryClient: QueryClient
  portfolioId?: number
}) => {
  queryClient.invalidateQueries({
    predicate: getPredicate([PAGINATED_PORTFOLIOS_QUERY_CACHE_KEY]),
  })
  queryClient.invalidateQueries({
    predicate: getPredicate([PORTFOLIO_QUERY_CACHE_KEY, portfolioId]),
  })
  queryClient.invalidateQueries({
    predicate: getPredicate([PORTFOLIO_SNAPSHOTS_QUERY_CACHE_KEY, portfolioId]),
  })
  queryClient.invalidateQueries({
    predicate: getPredicate([PORTFOLIO_SOURCES_QUERY_CACHE_KEY, portfolioId]),
  })
  queryClient.invalidateQueries({
    predicate: getPredicate([PORTFOLIO_DISCLOSURE_QUERY_CACHE_KEY, portfolioId]),
  })
  // Dashboard
  queryClient.invalidateQueries({
    predicate: getPredicate([PORTFOLIO_IMPACT_DATA_QUERY_CACHE_KEY, portfolioId]),
  })
  // SDGs
  queryClient.invalidateQueries({
    predicate: getPredicate([PORTFOLIO_SDG_ALIGNMENT_OUTCOMES_QUERY_CACHE_KEY, portfolioId]),
  })
  queryClient.invalidateQueries({
    predicate: getPredicate([
      PORTFOLIO_SDG_ALIGNMENT_REVENUE_WEIGHTED_QUERY_CACHE_KEY,
      portfolioId,
    ]),
  })
  queryClient.invalidateQueries({
    predicate: getPredicate([
      PORTFOLIO_SDG_ALIGNMENT_REVENUE_UNWEIGHTED_QUERY_CACHE_KEY,
      portfolioId,
    ]),
  })
  queryClient.invalidateQueries({
    predicate: getPredicate([
      PORTFOLIO_SDI_ALIGNMENT_REVENUE_WEIGHTED_QUERY_CACHE_KEY,
      portfolioId,
    ]),
  })
  queryClient.invalidateQueries({
    predicate: getPredicate([
      PORTFOLIO_SDI_ALIGNMENT_REVENUE_UNWEIGHTED_QUERY_CACHE_KEY,
      portfolioId,
    ]),
  })
  queryClient.invalidateQueries({
    predicate: getPredicate([PORTFOLIO_SDI_LABELS_WEIGHTED_QUERY_CACHE_KEY, portfolioId]),
  })
  queryClient.invalidateQueries({
    predicate: getPredicate([PORTFOLIO_SDI_LABELS_UNWEIGHTED_QUERY_CACHE_KEY, portfolioId]),
  })
  // Holdings
  queryClient.invalidateQueries({
    predicate: getPredicate([PAGINATED_HOLDINGS_QUERY_CACHE_KEY, portfolioId]),
  })
  // Holdings by company
  queryClient.invalidateQueries({
    predicate: getPredicate([PAGINATED_FOF_HOLDINGS_BY_COMPANY_QUERY_CACHE_KEY, portfolioId]),
  })
  // Data
  queryClient.invalidateQueries({
    predicate: getPredicate([ENTITY_DATA_BY_QUESTION_ID_QUERY_CACHE_KEY]),
  })
  // Entity breakdown by funds
  queryClient.invalidateQueries({
    predicate: getPredicate([ENTITY_BREAKDOWN_BY_FUNDS_QUERY_CACHE_KEY, portfolioId]),
  })
}

export const usePaginatedPortfolios = ({
  perPage,
  useUrlSync,
  defaultParams,
  enabled = true,
}: {
  perPage?: number
  useUrlSync?: boolean
  defaultParams?: {
    type?: PortfolioType
    portfolio_id?: string[]
  }
  enabled?: boolean
}) => {
  const { queryString, filterConfig, initialPaginationConfig } = useUrlApiConnector<Portfolio>({
    tableToApiFieldMap: reversePortfolioFieldMap,
    urlKey: PAGINATED_PORTFOLIOS_QUERY_CACHE_KEY,
    defaultParams,
    ...pickBy({ perPage, urlSyncEnabled: useUrlSync }, valueIsDefined),
  })

  const { data, ...rest } = useQuery({
    queryKey: [PAGINATED_PORTFOLIOS_QUERY_CACHE_KEY, queryString],
    queryFn: () => GeneratedFactApi.portfolios.listPortfolios({ query: { q: queryString } }),
    placeholderData: keepPreviousData,
    staleTime: apiConfig.defaultStaleTime,
    enabled,
  })

  const transformedPortfolios = useMemo(
    () =>
      data?.results ? data?.results.map((d) => transformPortfolio(snakeToCamelKeys(d))) : undefined,
    [data?.results],
  )

  const paginationConfig = getPaginationConfig({
    numResults: data?.total,
    paginationConfig: initialPaginationConfig,
  })

  return {
    ...rest,
    data: {
      ...data,
      results: transformedPortfolios,
    },
    filterConfig,
    paginationConfig,
  }
}

export const usePortfolio = ({
  portfolioId,
  snapshotId,
}: {
  portfolioId: number | undefined | null
  snapshotId?: number | undefined
}) => {
  const { data, ...rest } = useQuery({
    queryKey: [PORTFOLIO_QUERY_CACHE_KEY, portfolioId, snapshotId],
    queryFn: () =>
      portfolioId
        ? GeneratedFactApi.portfolios.getPortfolioWithHoldings({
            path: { portfolio_id: portfolioId },
            query: pickBy({ snapshot_id: snapshotId }, valueIsDefined),
          })
        : undefined,
    enabled: !!portfolioId,
    staleTime: apiConfig.defaultStaleTime,
  })

  const transformedPortfolio = useMemo(
    () => (data ? transformPortfolio(snakeToCamelKeys(data)) : undefined),
    [data],
  )

  return {
    ...rest,
    data: transformedPortfolio,
  }
}

export const usePortfolioSources = ({
  portfolioId,
  defaultParams,
  defaultSortBy,
  defaultSortReverse,
}: {
  portfolioId: number
  defaultParams?: { [key: string]: string | number | string[] }
  defaultSortBy?: keyof SourceWithEntityName
  defaultSortReverse?: boolean
}) => {
  const { queryString, filterConfig, initialPaginationConfig } =
    useUrlApiConnector<SourceWithEntityName>({
      tableToApiFieldMap: reverseSourceFieldMap,
      urlKey: PORTFOLIO_SOURCES_QUERY_CACHE_KEY,
      defaultParams,
      ...(defaultSortBy ? { defaultSortBy } : {}),
      ...(defaultSortReverse ? { defaultSortReverse } : {}),
    })

  const { data, ...rest } = useQuery({
    queryKey: [PORTFOLIO_SOURCES_QUERY_CACHE_KEY, queryString, portfolioId],

    queryFn: () =>
      portfolioId
        ? GeneratedFactApi.portfolios.listPortfolioSources({
            path: { portfolio_id: portfolioId },
            query: { q: queryString },
          })
        : undefined,

    enabled: !!portfolioId && !!queryString,
    staleTime: TWELVE_HOURS_MS,
    placeholderData: keepPreviousData,
  })

  const transformedSources = useMemo(
    () =>
      data?.results ? data?.results.map((d) => transformSource(snakeToCamelKeys(d))) : undefined,
    [data?.results],
  )

  const paginationConfig = getPaginationConfig({
    numResults: data?.total,
    paginationConfig: initialPaginationConfig,
  })

  return {
    ...rest,
    data: {
      ...data,
      results: transformedSources,
    },
    filterConfig,
    paginationConfig,
  }
}

export const usePortfolioDisclosure = ({
  portfolioId,
  snapshotId,
  selectedMetricConfigId,
  hasSnapshotsAccess,
}: {
  portfolioId: number
  snapshotId?: number | undefined
  selectedMetricConfigId: string | null
  hasSnapshotsAccess?: boolean
}) => {
  const { data, isFetching } = useQuery({
    queryKey: [
      PORTFOLIO_DISCLOSURE_QUERY_CACHE_KEY,
      portfolioId,
      snapshotId,
      selectedMetricConfigId,
      hasSnapshotsAccess,
    ],

    queryFn: () =>
      portfolioId && selectedMetricConfigId
        ? GeneratedFactApi.portfolios.getDisclosureData({
            path: { portfolio_id: portfolioId },
            query: {
              ...pickBy({ snapshot_id: snapshotId }, valueIsDefined),
              metrics_config_id: selectedMetricConfigId,
            },
            ...(hasSnapshotsAccess
              ? { headers: { 'x-np-feature': ['snapshot-attribution'] } }
              : {}),
          })
        : undefined,

    staleTime: TWELVE_HOURS_MS,
    enabled: !!portfolioId && !!selectedMetricConfigId,
  })

  return {
    data: snakeToCamelKeys(data),
    isFetching,
  }
}

export type PortfolioDisclosureData = ReturnType<typeof usePortfolioDisclosure>['data']
export type QuestionDisclosure = NonNullable<PortfolioDisclosureData>[number]
export type DisclosureYear = QuestionDisclosure['absoluteYears'][number]

const defaultUploadErrorMessage = 'An error occurred. Please check your file and try again.'

export const useUploadPortfolio = ({
  onSuccess,
  onError,
  xNpFeature,
  isFoFUpload = false,
}: {
  onSuccess: (token: string, portfolio: CreatePortfolio) => void
  onError: (err: string) => void
  xNpFeature?: string[]
  isFoFUpload?: boolean
}) => {
  const queryClient = useQueryClient()

  const upload = isFoFUpload ? 'uploadPortfolioWithFundHoldings' : 'uploadPortfolio'

  const { mutate: uploadPortfolio, isPending } = useMutation<
    { result_id: string },
    ApiError,
    CreatePortfolio,
    unknown
  >({
    mutationFn: (payload) =>
      GeneratedFactApi.portfolios[upload]({
        body: formatCreatePortfolioRequest(payload),
        ...(xNpFeature ? { headers: { 'x-np-feature': xNpFeature } } : {}),
      }),
    onSuccess: ({ result_id }, payload) => {
      // NOTE: the upload could be a new snapshot for an existing portfolio
      invalidateAllPortfolioQueries({ queryClient })
      onSuccess(result_id, payload)
    },
    onError: (err: ApiError) => onError(getApiErrorMessage(err, defaultUploadErrorMessage)),
  })

  return {
    uploadPortfolio,
    isPending,
  }
}

export const useUpdatePortfolio = ({
  portfolioId,
  snapshotId,
  onSuccess,
  onError,
}: {
  portfolioId: number
  snapshotId?: number | undefined
  onSuccess?: () => void
  onError: (err: string) => void
}) => {
  const queryClient = useQueryClient()

  const { mutate: updatePortfolio, isPending } = useMutation<
    BasePortfolio,
    ApiError,
    PortfolioUpdate,
    unknown
  >({
    mutationFn: (body) =>
      GeneratedFactApi.portfolios.updatePortfolio({
        body,
        path: { portfolio_id: portfolioId },
        query: pickBy({ snapshot_id: snapshotId }, valueIsDefined),
      }),
    onSuccess: () => {
      invalidateAllPortfolioQueries({ queryClient, portfolioId })
      onSuccess?.()
    },
    onError: (err: ApiError) => onError(getApiErrorMessage(err)),
  })

  return {
    updatePortfolio,
    isPending,
  }
}

export const useCopyPortfolio = ({
  portfolioId,
  onSuccess,
}: {
  portfolioId: number
  onSuccess: () => void
}) => {
  const { mutate: copyPortfolio, isPending } = useMutation<
    CopyPortfolioResponse,
    ApiError,
    CopyPortfolioRequest,
    unknown
  >({
    mutationFn: (body) =>
      GeneratedFactApi.portfolios.copyPortfolio({
        body,
        path: { portfolio_id: portfolioId },
      }),
    onSuccess,
  })

  return {
    copyPortfolio,
    isPending,
  }
}

type UsePortfolioSDGAlignment = {
  portfolioId: number | undefined
  snapshotId?: number | undefined
  isWeighted: boolean
  includeEstimations: boolean
  hasSnapshotsAccess?: boolean | undefined
}

const getPortfolioSdgOutcomesSummary = ({
  portfolioId,
  snapshotId,
  isWeighted,
  includeEstimations,
  hasSnapshotsAccess,
}: UsePortfolioSDGAlignment) =>
  portfolioId
    ? GeneratedFactApi.portfolios.getPortfolioSdgOutcomesSummary({
        path: { portfolio_id: portfolioId },
        query: {
          ...pickBy({ snapshot_id: snapshotId }, valueIsDefined),
          include_estimations: includeEstimations,
          is_weighted: isWeighted,
        },
        ...(hasSnapshotsAccess ? { headers: { 'x-np-feature': ['snapshot-attribution'] } } : {}),
      })
    : undefined

export const usePortfolioSDGAlignmentOutcomes = ({
  portfolioId,
  snapshotId,
  isWeighted,
  includeEstimations,
  enabled,
  hasSnapshotsAccess,
}: UsePortfolioSDGAlignment & { enabled: boolean }) => {
  const { data, ...rest } = useQuery({
    queryKey: [
      PORTFOLIO_SDG_ALIGNMENT_OUTCOMES_QUERY_CACHE_KEY,
      portfolioId,
      snapshotId,
      isWeighted,
      includeEstimations,
      hasSnapshotsAccess,
    ],
    queryFn: () =>
      getPortfolioSdgOutcomesSummary({
        portfolioId,
        snapshotId,
        isWeighted,
        includeEstimations,
        hasSnapshotsAccess,
      }),
    enabled: enabled && !!portfolioId,
    staleTime: TWELVE_HOURS_MS,
  })

  const queryClient = useQueryClient()

  // Make sure the initial query has completed successfully.
  if (data) {
    // By prefetching the query with the inverse of the current isWeighted and includeEstimations value,
    // we can avoid two separate loading states if the AUM/Holdings tab is switched
    // before the data becomes stale.
    queryClient.prefetchQuery({
      queryKey: [
        PORTFOLIO_SDG_ALIGNMENT_OUTCOMES_QUERY_CACHE_KEY,
        portfolioId,
        snapshotId,
        !isWeighted,
        includeEstimations,
        hasSnapshotsAccess,
      ],
      queryFn: () =>
        getPortfolioSdgOutcomesSummary({
          portfolioId,
          snapshotId,
          isWeighted: !isWeighted,
          includeEstimations,
          hasSnapshotsAccess,
        }),
      staleTime: apiConfig.defaultStaleTime,
    })

    queryClient.prefetchQuery({
      queryKey: [
        PORTFOLIO_SDG_ALIGNMENT_OUTCOMES_QUERY_CACHE_KEY,
        portfolioId,
        snapshotId,
        isWeighted,
        !includeEstimations,
        hasSnapshotsAccess,
      ],
      queryFn: () =>
        getPortfolioSdgOutcomesSummary({
          portfolioId,
          snapshotId,
          isWeighted,
          includeEstimations: !includeEstimations,
          hasSnapshotsAccess,
        }),
      staleTime: apiConfig.defaultStaleTime,
    })
  }

  return {
    ...rest,
    data: snakeToCamelKeys(data),
  }
}

export type PortfolioSDGAlignmentOutcomes = NonNullable<
  ReturnType<typeof usePortfolioSDGAlignmentOutcomes>['data']
>

const usePortfolioSDGAlignmentRevenueWeighted = ({
  portfolioId,
  snapshotId,
  enabled,
  hasSnapshotsAccess,
}: {
  portfolioId: number | undefined
  snapshotId?: number | undefined
  enabled: boolean
  hasSnapshotsAccess?: boolean | undefined
}) => {
  const { data, ...rest } = useQuery({
    queryKey: [
      PORTFOLIO_SDG_ALIGNMENT_REVENUE_WEIGHTED_QUERY_CACHE_KEY,
      portfolioId,
      snapshotId,
      hasSnapshotsAccess,
    ],
    queryFn: () =>
      portfolioId
        ? GeneratedFactApi.portfolios.getPortfolioSdgRevenueSummaryWeighted({
            path: { portfolio_id: portfolioId },
            query: pickBy({ snapshot_id: snapshotId }, valueIsDefined),
            ...(hasSnapshotsAccess
              ? { headers: { 'x-np-feature': ['snapshot-attribution'] } }
              : {}),
          })
        : undefined,
    enabled,
    staleTime: TWELVE_HOURS_MS,
  })

  return {
    ...rest,
    data: snakeToCamelKeys(data),
  }
}

export type PortfolioSDGAlignmentRevenueWeighted = NonNullable<
  ReturnType<typeof usePortfolioSDGAlignmentRevenueWeighted>['data']
>

const usePortfolioSDGAlignmentRevenueUnweighted = ({
  portfolioId,
  snapshotId,
  enabled,
  hasSnapshotsAccess,
}: {
  portfolioId: number | undefined
  snapshotId?: number | undefined
  enabled: boolean
  hasSnapshotsAccess?: boolean | undefined
}) => {
  const { data, ...rest } = useQuery({
    queryKey: [
      PORTFOLIO_SDG_ALIGNMENT_REVENUE_UNWEIGHTED_QUERY_CACHE_KEY,
      portfolioId,
      snapshotId,
      hasSnapshotsAccess,
    ],
    queryFn: () =>
      portfolioId
        ? GeneratedFactApi.portfolios.getPortfolioSdgRevenueSummaryUnweighted({
            path: { portfolio_id: portfolioId },
            query: pickBy({ snapshot_id: snapshotId }, valueIsDefined),
            ...(hasSnapshotsAccess
              ? { headers: { 'x-np-feature': ['snapshot-attribution'] } }
              : {}),
          })
        : undefined,
    enabled,
    staleTime: TWELVE_HOURS_MS,
  })

  return {
    ...rest,
    data: snakeToCamelKeys(data),
  }
}

export type PortfolioSDGAlignmentRevenueUnweighted = NonNullable<
  ReturnType<typeof usePortfolioSDGAlignmentRevenueUnweighted>['data']
>

const usePortfolioSDIAlignmentRevenueWeighted = ({
  portfolioId,
  snapshotId,
  enabled,
}: {
  portfolioId: number | undefined
  snapshotId?: number | undefined
  enabled: boolean
}) => {
  const { data, ...rest } = useQuery({
    queryKey: [PORTFOLIO_SDI_ALIGNMENT_REVENUE_WEIGHTED_QUERY_CACHE_KEY, portfolioId, snapshotId],
    queryFn: () =>
      portfolioId
        ? GeneratedFactApi.portfolios.getPortfolioSdiRevenueSummaryWeighted({
            path: { portfolio_id: portfolioId },
            query: pickBy({ snapshot_id: snapshotId }, valueIsDefined),
          })
        : undefined,
    enabled,
    staleTime: TWELVE_HOURS_MS,
  })

  return {
    ...rest,
    data: snakeToCamelKeys(data),
  }
}

export type PortfolioSDIAlignmentRevenueWeighted = NonNullable<
  ReturnType<typeof usePortfolioSDIAlignmentRevenueWeighted>['data']
>

const usePortfolioSDIAlignmentRevenueUnweighted = ({
  portfolioId,
  snapshotId,
  enabled,
}: {
  portfolioId: number | undefined
  snapshotId?: number | undefined
  enabled: boolean
}) => {
  const { data, ...rest } = useQuery({
    queryKey: [PORTFOLIO_SDI_ALIGNMENT_REVENUE_UNWEIGHTED_QUERY_CACHE_KEY, portfolioId, snapshotId],
    queryFn: () =>
      portfolioId
        ? GeneratedFactApi.portfolios.getPortfolioSdiRevenueSummaryUnweighted({
            path: { portfolio_id: portfolioId },
            query: pickBy({ snapshot_id: snapshotId }, valueIsDefined),
          })
        : undefined,
    enabled,
    staleTime: TWELVE_HOURS_MS,
  })

  return {
    ...rest,
    data: snakeToCamelKeys(data),
  }
}

export type PortfolioSDIAlignmentRevenueUnweighted = NonNullable<
  ReturnType<typeof usePortfolioSDIAlignmentRevenueUnweighted>['data']
>

export const usePortfolioAlignmentRevenue = ({
  portfolioId,
  snapshotId,
  isWeighted,
  isSdiRevenue,
  enabled,
  hasSnapshotsAccess,
}: {
  portfolioId: number | undefined
  snapshotId?: number | undefined
  isWeighted: boolean
  isSdiRevenue: boolean
  enabled: boolean
  hasSnapshotsAccess?: boolean
}) => {
  const sdgWeighted = usePortfolioSDGAlignmentRevenueWeighted({
    portfolioId,
    snapshotId,
    enabled: enabled && !isSdiRevenue,
    hasSnapshotsAccess,
  })

  const sdgUnweighted = usePortfolioSDGAlignmentRevenueUnweighted({
    portfolioId,
    snapshotId,
    enabled: enabled && !isSdiRevenue,
    hasSnapshotsAccess,
  })

  const sdiWeighted = usePortfolioSDIAlignmentRevenueWeighted({
    portfolioId,
    snapshotId,
    enabled: enabled && isSdiRevenue,
  })

  const sdiUnweighted = usePortfolioSDIAlignmentRevenueUnweighted({
    portfolioId,
    snapshotId,
    enabled: enabled && isSdiRevenue,
  })

  const weighted = isSdiRevenue ? sdiWeighted : sdgWeighted
  const unweighted = isSdiRevenue ? sdiUnweighted : sdgUnweighted
  const { data, isFetching, isError } = isWeighted ? weighted : unweighted

  return { data, isFetching, isError }
}

export const usePortfolioSDILabelsWeighted = ({
  portfolioId,
  snapshotId,
  enabled,
}: {
  portfolioId: number | undefined
  snapshotId?: number | undefined
  enabled: boolean
}) => {
  const { data, ...rest } = useQuery({
    queryKey: [PORTFOLIO_SDI_LABELS_WEIGHTED_QUERY_CACHE_KEY, portfolioId, snapshotId],
    queryFn: () =>
      portfolioId
        ? GeneratedFactApi.portfolios.getPortfolioLabelsWeighted({
            path: { portfolio_id: portfolioId },
            query: pickBy({ snapshot_id: snapshotId }, valueIsDefined),
          })
        : undefined,
    enabled,
    staleTime: TWELVE_HOURS_MS,
  })

  return {
    ...rest,
    data: snakeToCamelKeys(data),
  }
}

export type PortfolioSDILabelsWeighted = NonNullable<
  ReturnType<typeof usePortfolioSDILabelsWeighted>['data']
>

export const usePortfolioSDILabelsUnweighted = ({
  portfolioId,
  snapshotId,
  enabled,
}: {
  portfolioId: number | undefined
  snapshotId?: number | undefined
  enabled: boolean
}) => {
  const { data, ...rest } = useQuery({
    queryKey: [PORTFOLIO_SDI_LABELS_UNWEIGHTED_QUERY_CACHE_KEY, portfolioId, snapshotId],
    queryFn: () =>
      portfolioId
        ? GeneratedFactApi.portfolios.getPortfolioLabelsUnweighted({
            path: { portfolio_id: portfolioId },
            query: pickBy({ snapshot_id: snapshotId }, valueIsDefined),
          })
        : undefined,
    enabled,
    staleTime: TWELVE_HOURS_MS,
  })

  return {
    ...rest,
    data: snakeToCamelKeys(data),
  }
}

export type PortfolioSDILabelsUnweighted = NonNullable<
  ReturnType<typeof usePortfolioSDILabelsUnweighted>['data']
>

export const useExportPortfolio = ({
  portfolio,
  snapshotId,
  selectedMetricConfigId,
  requestPortfolioExport,
  hasSnapshotsAccess,
}: {
  portfolio: { id: number; name: string }
  snapshotId?: number | undefined
  selectedMetricConfigId: string
  requestPortfolioExport: (
    resultId: string,
    portfolioId: number,
    portfolioName: string,
    metricConfigId: string,
  ) => void
  hasSnapshotsAccess?: boolean
}) =>
  useMutation({
    mutationFn: () =>
      GeneratedFactApi.portfolios.exportPortfolio({
        body: {
          portfolio_id: portfolio.id,
          ...pickBy({ snapshot_id: snapshotId }, valueIsDefined),
          export_options: {
            metrics_config_id: selectedMetricConfigId,
            should_send_email: true,
          },
        },
        ...(hasSnapshotsAccess ? { headers: { 'x-np-feature': ['snapshot-attribution'] } } : {}),
      }),
    onSuccess: ({ result_id }) =>
      requestPortfolioExport(result_id, portfolio.id, portfolio.name, selectedMetricConfigId),
  })

export const useExportPortfolioPAIs = ({
  portfolioId,
  snapshotId,
  portfolioName,
  requestPortfolioPAIsExport,
  hasSnapshotsAccess,
}: {
  portfolioId: number
  snapshotId?: number | undefined
  portfolioName: string
  requestPortfolioPAIsExport: (resultId: string, portfolioId: number, portfolioName: string) => void
  hasSnapshotsAccess?: boolean
}) =>
  useMutation({
    mutationFn: () =>
      GeneratedFactApi.portfolios.exportPortfolioPai({
        body: {
          portfolio_id: portfolioId,
          ...pickBy({ snapshot_id: snapshotId }, valueIsDefined),
          export_options: { should_send_email: true },
        },
        ...(hasSnapshotsAccess ? { headers: { 'x-np-feature': ['snapshot-attribution'] } } : {}),
      }),
    onSuccess: ({ result_id }) => requestPortfolioPAIsExport(result_id, portfolioId, portfolioName),
  })

export const useExportPortfolioSDGs = ({
  portfolioId,
  snapshotId,
  portfolioName,
  requestPortfolioSDGsExport,
  withEstimations = false,
}: {
  portfolioId: number
  snapshotId?: number | undefined
  portfolioName: string
  requestPortfolioSDGsExport: (resultId: string, portfolioId: number, portfolioName: string) => void
  withEstimations?: boolean
}) =>
  useMutation({
    mutationFn: () =>
      GeneratedFactApi.portfolios.exportPortfolioSdg({
        body: {
          portfolio_id: portfolioId,
          ...pickBy({ snapshot_id: snapshotId }, valueIsDefined),
          export_options: { should_send_email: true, with_estimations: withEstimations },
        },
      }),
    onSuccess: ({ result_id }) => requestPortfolioSDGsExport(result_id, portfolioId, portfolioName),
  })

export const useExportPortfolioSDGRevenue = ({
  portfolioId,
  snapshotId,
  portfolioName,
  requestPortfolioSDGRevenueExport,
}: {
  portfolioId: number
  snapshotId?: number | undefined
  portfolioName: string
  requestPortfolioSDGRevenueExport: (
    resultId: string,
    portfolioId: number,
    portfolioName: string,
  ) => void
}) =>
  useMutation({
    mutationFn: () =>
      GeneratedFactApi.portfolios.exportPortfolioSdgRevenue({
        body: {
          portfolio_id: portfolioId,
          ...pickBy({ snapshot_id: snapshotId }, valueIsDefined),
          export_options: { should_send_email: true },
        },
      }),
    onSuccess: ({ result_id }) =>
      requestPortfolioSDGRevenueExport(result_id, portfolioId, portfolioName),
  })

export const useExportPortfolioDashboard = ({
  portfolioId,
  snapshotId,
  portfolioName,
  metricConfigId,
  metricConfigName,
  hasSnapshotsAccess,
}: {
  portfolioId: number
  snapshotId?: number | undefined
  portfolioName: string
  metricConfigId: string
  metricConfigName: string
  hasSnapshotsAccess?: boolean
}) => {
  return useStreamDownload({
    url: `${apiConfig.factsHost}/api/v1/portfolios/dashboard/export`,
    downloadName: `${portfolioName} - Dashboard - ${metricConfigName}`,
    body: JSON.stringify({
      portfolio_id: portfolioId,
      ...pickBy({ snapshot_id: snapshotId }, valueIsDefined),
      export_options: { metrics_config_id: metricConfigId },
    }),
    headers: {
      ...(hasSnapshotsAccess ? { 'x-np-feature': 'snapshot-attribution' } : {}),
    },
  })
}

export const useExportPortfolioData = ({
  portfolioId,
  snapshotId,
  portfolioName,
  theme,
  questionId,
  questionName,
  hasSnapshotsAccess,
}: {
  portfolioId: number
  snapshotId?: number | undefined
  portfolioName: string
  theme: string
  questionId: number
  questionName: string
  hasSnapshotsAccess?: boolean
}) => {
  return useStreamDownload({
    url: `${apiConfig.factsHost}/api/v1/portfolios/data/export`,
    downloadName: `${portfolioName} - Data - ${theme} - ${questionName}`,
    body: JSON.stringify({
      portfolio_id: portfolioId,
      ...pickBy({ snapshot_id: snapshotId }, valueIsDefined),
      question_id: questionId,
    }),
    headers: {
      ...(hasSnapshotsAccess ? { 'x-np-feature': 'snapshot-attribution' } : {}),
    },
  })
}

export const useExportPortfolioHoldings = ({
  portfolioId,
  portfolioName,
  snapshotId,
}: {
  portfolioId: number
  portfolioName: string
  snapshotId?: number | undefined
}) => {
  return useStreamDownload({
    url: `${apiConfig.factsHost}/api/v1/holdings/export`,
    downloadName: `${portfolioName} - Holdings${snapshotId ? ' - Snapshot' + snapshotId : ''}`,
    body: JSON.stringify({
      portfolio_id: portfolioId,
      ...pickBy({ snapshot_id: snapshotId }, valueIsDefined),
    }),
  })
}

export const useDeletePortfolio = ({ onError }: { onError: (err: string) => void }) => {
  const { mutate: deletePortfolio, isPending } = useMutation<
    unknown,
    ApiError,
    { portfolioId: number },
    unknown
  >({
    mutationFn: ({ portfolioId }) =>
      GeneratedFactApi.portfolios.deletePortfolio({ path: { portfolio_id: portfolioId } }),
    onError: (err: ApiError) => onError(getApiErrorMessage(err)),
  })

  return {
    deletePortfolio,
    isPending,
  }
}

export const useListPortfolioSnapshots = ({
  portfolioId,
  enabled = true,
}: {
  portfolioId: number | undefined
  enabled?: boolean
}) => {
  const { queryString } = useUrlApiConnector({
    tableToApiFieldMap: reversePortfolioSnapshotFieldMap,
    urlKey: PORTFOLIO_SNAPSHOTS_QUERY_CACHE_KEY,
    defaultSortBy: 'valuationDate',
    defaultSortReverse: true,
    urlSyncEnabled: false,
  })

  return useQuery({
    queryKey: [PORTFOLIO_SNAPSHOTS_QUERY_CACHE_KEY, portfolioId, queryString],
    queryFn: () =>
      portfolioId
        ? GeneratedFactApi.portfolios
            .listPortfolioSnapshots({
              path: { portfolio_id: portfolioId },
              query: { q: queryString },
            })
            .then(snakeToCamelKeys)
        : undefined,
    enabled: !!portfolioId && enabled,
    staleTime: TWELVE_HOURS_MS,
  })
}

export const useGetOnePortfolio = () => {
  const { data, isPending } = useQuery({
    queryKey: ['getOnePortfolio'],
    queryFn: () => GeneratedFactApi.portfolios.listPortfolios({ query: { q: 'limit=1' } }),
    staleTime: TWELVE_HOURS_MS,
  })

  const result = data?.results?.[0]
  const portfolio = result ? transformPortfolio(snakeToCamelKeys(result)) : undefined
  return { portfolio, isPending }
}
